import type {
  DataTableCellEditCompleteEvent,
  DataTableCellEditInitEvent,
} from 'primevue/datatable';
import { get, isEmpty, isEqual, set } from 'lodash-es';
import type { Item, Modifier, RecordEntityId } from '@centric-os/types';

export default () => {
  const initialValues = new Map<RecordEntityId, number>(); // cell edit is currently only available for price

  const handleTableCellEditInit = (event: DataTableCellEditInitEvent): void => {
    // Store the initial value for rollback in the event of an API failure
    const { data, field } = event;
    const initialValue = get(data, field);
    initialValues.set(data?.id, initialValue);
  };

  const handleTableCellEditComplete = async (
    event: DataTableCellEditCompleteEvent,
    updateCallback: (data: any) => Promise<unknown>,
  ): Promise<void> => {
    const { data, newData, field } = event;

    // Early return if triggered by tabbing and has no initial values
    if (isEmpty(initialValues)) return;

    const initialValue: number = initialValues.get(data.id);
    const newValue: number = get(newData, field);

    // Early return if value hasn't changed
    if (newValue === undefined || isEqual(initialValue, newValue)) {
      initialValues.delete(data.id);
      return;
    }

    // Send only the edited field (+ id) rather than the whole object: a full snapshot risks PATCHing stale data when edits happen in quick succession.
    const lastIndex: number = field.lastIndexOf('.');
    const isRelationship: boolean = lastIndex > 0;
    const leafField: string = isRelationship ? field.substring(lastIndex + 1) : field;
    const entity: Partial<Item> | Partial<Modifier> = isRelationship
      ? get(newData, field.substring(0, lastIndex))
      : newData;
    const payload: Partial<Item> | Partial<Modifier> = {
      id: entity?.id,
      [leafField]: newValue,
    };

    try {
      await updateCallback(payload);
    } catch (error) {
      set(data, field, initialValue);
      throw error;
    } finally {
      initialValues.delete(data?.id);
    }
  };

  return {
    handleTableCellEditInit,
    handleTableCellEditComplete,
  };
};
